import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './PermittedDomainsEditor.scss';
import { TagInput, Button, Icon } from 'design-system-web';

interface PermittedDomainsEditorProps {
    domains: string[];
    onSave: (domains: string[]) => Promise<{ success: boolean; error?: Error }>;
    onCancel: () => void;
    onChange?: (domains: string[]) => void; // Optional callback for auto-save scenarios
    hideSaveCancel?: boolean; // Hide Save/Cancel buttons for always-editable mode
    id?: string; // Optional custom ID for TagInput
    loading?: boolean; // Loading state from mutation
    error?: Error | null; // Error state from mutation
    onValidationErrorChange?: (hasError: boolean) => void; // Callback to report validation errors to parent
}

export const PermittedDomainsEditor: React.FC<PermittedDomainsEditorProps> = ({
    domains: initialDomains,
    onSave,
    onCancel,
    onChange,
    hideSaveCancel = false,
    id = 'tenant-permitted-domains-input',
    loading = false,
    error: _saveError = null,
    onValidationErrorChange,
}) => {
    const { t } = useTranslation();
    const [domains, setDomains] = useState<string[]>(initialDomains);
    const [hasDuplicates, setHasDuplicates] = useState(false);
    const lastValidDomainsRef = useRef<string[]>(initialDomains);
    const isInternalUpdateRef = useRef(false);

    // Sync domains when initialDomains prop changes externally (not from our own updates)
    useEffect(() => {
        // Only sync if this is an external change (not from our internal state updates)
        if (!isInternalUpdateRef.current) {
            const domainsChanged = 
                lastValidDomainsRef.current.length !== initialDomains.length ||
                lastValidDomainsRef.current.some((domain, index) => domain !== initialDomains[index]);

            if (domainsChanged) {
                setDomains(initialDomains);
                lastValidDomainsRef.current = initialDomains;
                setHasDuplicates(false);
                onValidationErrorChange?.(false);
            }
        }

        isInternalUpdateRef.current = false;
    }, [initialDomains, onValidationErrorChange]);

    // Handle validation change from TagInput - update local state and notify parent
    const handleValidationChange = useCallback((hasError: boolean) => {
        setHasDuplicates(hasError);
        onValidationErrorChange?.(hasError);
    }, [onValidationErrorChange]);

    // Duplicate detection: case-insensitive
    const validateDomain = useCallback((domain: string): boolean => {
        const normalizedDomain = domain.toLowerCase().trim();
        const duplicateCount = domains.filter(
            d => d.toLowerCase().trim() === normalizedDomain
        ).length;
        
        // Allow if no duplicates, or if this is the only occurrence
        return duplicateCount <= 1;
    }, [domains]);

    // Handle domain changes - always update state for display, but only propagate valid changes
    const handleDomainsChange = useCallback((newDomains: string[]) => {
        // Always update domains state so TagInput can display them (including errors)
        isInternalUpdateRef.current = true;
        setDomains(newDomains);

        // Check for duplicates in the new domains
        const normalizedDomains = newDomains.map(d => d.toLowerCase().trim());
        const hasDupes = normalizedDomains.some((domain, index) => 
            normalizedDomains.indexOf(domain) !== index && domain !== ''
        );

        if (!hasDupes) {
            // No duplicates - update valid state and propagate to parent
            lastValidDomainsRef.current = newDomains;
            setHasDuplicates(false);
            onValidationErrorChange?.(false);
            
            if (onChange) {
                onChange(newDomains);
            }
        } else {
            // Has duplicates - don't propagate to parent, but keep state for error display
            setHasDuplicates(true);
            onValidationErrorChange?.(true);
        }
    }, [onChange, onValidationErrorChange]);

    const handleSave = async () => {
        if (hasDuplicates || loading) {
            return;
        }

        await onSave(domains);
    };

    const handleCancel = () => {
        if (loading) {
            return;
        }

        setDomains(initialDomains);
        onCancel();
    };

    const handleAddClick = () => {
        // Find the TagInput's internal input and trigger Enter keydown to add the current value
        const input = document.querySelector(`#${id} [data-test="tagInput"]`) as HTMLInputElement;
        
        if (input && input.value.trim()) {

            // Create and dispatch Enter keydown event
            const enterEvent = new KeyboardEvent('keydown', {
                key: 'Enter',
                code: 'Enter',
                keyCode: 13,
                which: 13,
                bubbles: true,
                cancelable: true,
            });
            
            input.dispatchEvent(enterEvent);
            input.focus();
        }
    };

    return (
        <div className={styles.Editor} data-testid="permitted-domains-editor">
            <div className={styles.TagInputWithButton}>
                <div className={styles.TagInputContainer}>
                    <label className={styles.AddDomainLabel} htmlFor={id}>
                        {t('web.admin.channel.profile.settings.permittedDomains.label')}
                    </label>
                    <TagInput
                        tags={domains}
                        setTags={handleDomainsChange}
                        validateTag={validateDomain}
                        placeholder={t('web.admin.channel.profile.settings.permittedDomains.placeholder')}
                        errorText={t('web.admin.channel.profile.settings.permittedDomains.duplicateError')}
                        onValidationChange={handleValidationChange}
                        id={id}
                    />
                    {!hasDuplicates && (
                        <p className={styles.HelperText}>
                            {t('web.admin.channel.profile.settings.permittedDomains.helper')}
                        </p>
                    )}
                </div>
                <Button
                    variant="secondary"
                    size="medium"
                    onClick={handleAddClick}
                    testId="addDomainButton"
                    startIcon={<Icon size="small" name="plus" />}
                >
                    {t('web.admin.channel.profile.settings.permittedDomains.addButton')}
                </Button>
            </div>

            {!hideSaveCancel && (
                <div className={styles.ButtonGroup}>
                    <Button
                        variant="primary"
                        size="large"
                        onClick={handleSave}
                        disabled={hasDuplicates || loading}
                        loading={loading}
                        testId="savePermittedDomainsButton"
                    >
                        {loading ? t('web.admin.channel.profile.settings.permittedDomains.saving') : t('web.admin.channel.profile.settings.permittedDomains.saveButton')}
                    </Button>
                    <Button
                        variant="secondary"
                        size="large"
                        onClick={handleCancel}
                        disabled={loading}
                        testId="cancelPermittedDomainsButton"
                    >
                        {t('web.admin.channel.profile.settings.permittedDomains.cancelButton')}
                    </Button>
                </div>
            )}
        </div>
    );
};

